import axios from 'axios'
import { Message } from 'element-ui'
import appConfig from '@/config'

/**
 * 通用axios实例配置
 * 用于所有API请求
 */
const request = axios.create({
  baseURL: appConfig.baseURL,
  timeout: appConfig.timeout,
  withCredentials: false, // 官网不需要携带cookie
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
})

/**
 * 请求拦截器
 * 功能：设置请求头、日志记录
 */
request.interceptors.request.use(
  config => {
    // 开发环境下打印请求信息
    if (appConfig.isDev) {
      console.log(`[请求] ${config.method?.toUpperCase()} ${config.url}`, config.data || config.params)
    }

    // 设置安全相关的请求头
    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    
    // POST请求确保数据格式正确
    if (config.method === 'post' && config.data) {
      // 如果是FormData，修改Content-Type
      if (config.data instanceof FormData) {
        config.headers['Content-Type'] = 'multipart/form-data'
      }
    }

    return config
  },
  error => {
    // 请求错误处理
    console.error('[请求错误]', error)
    Message.error('请求发送失败，请检查网络连接')
    return Promise.reject(error)
  }
)

/**
 * 响应拦截器
 * 功能：统一错误处理、业务状态码处理、数据格式化
 */
request.interceptors.response.use(
  response => {
    // 开发环境下打印响应信息
    if (appConfig.isDev) {
      console.log(`[响应] ${response.config.url}`, response.data)
    }

    const res = response.data

    // 根据实际后端返回的数据结构进行调整
    // 假设后端返回格式为: { code: 0, data: {}, msg: '' }
    
    // 如果响应成功（根据业务状态码判断）
    if (res.code === 0 || res.code === 200 || response.status === 200) {
      return res
    }

    // 业务错误处理
    const errorMsg = res.msg || res.message || '请求失败'
    
    // 不同错误码的处理
    switch (res.code) {
      case 400:
        Message.error(errorMsg || '请求参数错误')
        break
      case 404:
        Message.error('请求的资源不存在')
        break
      case 500:
        Message.error('服务器内部错误，请稍后重试')
        break
      default:
        Message.error(errorMsg)
    }

    return Promise.reject(new Error(errorMsg))
  },
  error => {
    console.error('[响应错误]', error)

    let errorMessage = '网络请求失败'

    if (error.response) {
      // 服务器返回错误状态码
      const status = error.response.status
      const data = error.response.data

      switch (status) {
        case 400:
          errorMessage = data?.msg || '请求参数错误'
          break
        case 401:
          errorMessage = '未授权，请重新登录'
          break
        case 403:
          errorMessage = '拒绝访问'
          break
        case 404:
          errorMessage = '请求的资源不存在'
          break
        case 405:
          errorMessage = '请求方法不允许'
          break
        case 408:
          errorMessage = '请求超时'
          break
        case 500:
          errorMessage = '服务器内部错误'
          break
        case 502:
          errorMessage = '网关错误'
          break
        case 503:
          errorMessage = '服务不可用'
          break
        case 504:
          errorMessage = '网关超时'
          break
        default:
          errorMessage = data?.msg || `服务器错误(${status})`
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      if (error.code === 'ECONNABORTED') {
        errorMessage = '请求超时，请检查网络连接'
      } else if (error.message === 'Network Error') {
        errorMessage = '网络连接失败，请检查您的网络'
      } else {
        errorMessage = '网络异常，请稍后重试'
      }
    } else {
      // 请求配置出错
      errorMessage = error.message || '请求配置错误'
    }

    // 显示错误提示（避免在开发环境重复提示）
    if (!appConfig.isDev || !error.config?.__isRetryRequest) {
      Message.error(errorMessage)
    }

    return Promise.reject(error)
  }
)

export default request
